import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createJobPosting, deleteJobPosting, fetchJobById, fetchJobsList, updateJobPostingStatus, type JobsListQueryParams } from '@/api/jobs.api'
import { useToast } from '@/app/context/toast-provider'
import { jobsQueryKeys } from '@/constants/cache-keys.contant'
import { invalidateJobsList } from '@/lib/api/query-invalidation'
import type { IJob, QueryType } from '@/app/types/types'
import { useOptionalPlanLimitGuard } from '@/components/providers'

export const useJobsListQuery = (params: JobsListQueryParams, options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: jobsQueryKeys.list(params),
        queryFn: () => fetchJobsList({ ...params, showAnalytics: params.showAnalytics ?? 'true' }),
        enabled: options?.enabled !== false,
        staleTime: 0,
        // Server-side pagination changes the query key; without this, `data` briefly clears,
        // rowCount drops to 0, and MUI DataGrid snaps pagination back to page 0.
        placeholderData: keepPreviousData,
    })
}

export const useJobQuery = (id: number | null | undefined, companySlug: string, params: QueryType = {}, options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: jobsQueryKeys.detail(id ?? 0, companySlug),
        queryFn: () => fetchJobById(id!, companySlug, { showAnalytics: true, ...params }),
        enabled: options?.enabled !== false && Boolean(id) && Boolean(companySlug),
    })
}

export const useCreateJobMutation = () => {
    const queryClient = useQueryClient()
    const { showSuccessToast } = useToast()
    const planLimitGuard = useOptionalPlanLimitGuard()

    return useMutation({
        mutationFn: async (data: QueryType) => {
            if (planLimitGuard) {
                const okTotal = await planLimitGuard.guardPlanLimit('maxJobsTotal', 1)
                if (!okTotal) throw new Error('Plan limit reached for maxJobsTotal')
                const okActive = await planLimitGuard.guardPlanLimit('maxActiveJobs', 1)
                if (!okActive) throw new Error('Plan limit reached for maxActiveJobs')
            }
            return createJobPosting(data)
        },
        throwOnError: false,
        onSuccess: ({ message }) => {
            showSuccessToast(message ?? 'Job created successfully')
            void invalidateJobsList(queryClient)
            planLimitGuard?.invalidateUsage()
        },
    })
}

export const useUpdateJobStatusMutation = () => {
    const queryClient = useQueryClient()
    const { showSuccessToast, showErrorToast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: IJob | QueryType; disableToast?: boolean }) => updateJobPostingStatus(id, data),
        onSuccess: (_, variables) => {
            if (!variables.disableToast) {
                showSuccessToast('Job status updated successfully')
            }
            void invalidateJobsList(queryClient)
        },
        onError: (error: Error, variables) => {
            if (!variables.disableToast) {
                showErrorToast(error.message || 'Failed to update job status')
            }
        },
    })
}

export const useDeleteJobMutation = () => {
    const queryClient = useQueryClient()
    const { showSuccessToast, showErrorToast } = useToast()

    return useMutation({
        mutationFn: ({ id, params }: { id: number; params?: QueryType }) => deleteJobPosting(id, params ?? {}),
        onSuccess: ({ message }) => {
            showSuccessToast(message ?? 'Job deleted successfully')
            void invalidateJobsList(queryClient)
        },
        onError: (error: Error) => {
            showErrorToast(error.message || 'Failed to delete job')
        },
    })
}
